import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { useAuth } from "@/auth/AuthContext";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";

const CATS = ["meeting","reporting","financial","insurance","training","safety","records"];
const REC = ["annual","biannual","quarterly","monthly","once","on_trigger"];
const SEV = ["critical","important","recommended"];

export default function AdminTemplates() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState("NSW");
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.system_role !== "platform_admin") {
      navigate("/dashboard");
    }
  }, [user, authLoading, navigate]);

  const load = async () => {
    try {
      const { data } = await api.get(`/admin/compliance/templates?state=${state}&include_inactive=true`);
      setItems(data);
    } catch (err) { toast.error(apiError(err)); }
  };
  useEffect(() => { load(); }, [state]); // eslint-disable-line

  const openCreate = () => { setEditing({}); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setOpen(true); };

  const save = async (form) => {
    try {
      if (editing?.template_id) {
        await api.patch(`/admin/compliance/templates/${editing.template_id}`, form);
        toast.success("Template updated");
      } else {
        await api.post("/admin/compliance/templates", { ...form, state });
        toast.success("Template created");
      }
      setOpen(false); setEditing(null); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const deactivate = async (t) => {
    if (!window.confirm(`Deactivate "${t.title}"?`)) return;
    try {
      await api.post(`/admin/compliance/templates/${t.template_id}/deactivate`);
      toast.success("Deactivated"); load();
    } catch (err) { toast.error(apiError(err)); }
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8 lg:py-10" data-testid="admin-templates-page">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
          <div>
            <span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] mb-2" style={{ color: "var(--blue-dark)" }}>
              Platform admin
            </span>
            <h1 className="text-2xl font-bold" style={{ color: "var(--navy)" }}>Compliance template library</h1>
            <p className="text-sm" style={{ color: "var(--slate)" }}>Edit drives every new scheme's auto-generated obligations.</p>
          </div>
          <div className="flex items-center gap-2">
            <Select value={state} onValueChange={setState}>
              <SelectTrigger className="w-32" data-testid="admin-state-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="NSW">NSW</SelectItem>
                <SelectItem value="QLD">QLD</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={openCreate} style={{ background: "var(--blue)", color: "white" }} className="font-semibold" data-testid="admin-create-btn">
              + New template
            </Button>
          </div>
        </div>

        <div className="rounded-xl border bg-white overflow-hidden" style={{ borderColor: "var(--border)" }}>
          {items.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm" style={{ color: "var(--tagline-grey)" }}>No templates for {state}.</div>
          ) : items.map((t) => (
            <div key={t.template_id} className="flex items-center gap-4 px-5 py-4 border-t first:border-t-0" style={{ borderColor: "var(--border)", opacity: t.active ? 1 : 0.55 }} data-testid={`template-row-${t.template_id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm" style={{ color: "var(--navy)" }}>{t.title} {!t.active && <span className="text-[10px] uppercase ml-1" style={{ color: "var(--tagline-grey)" }}>(inactive)</span>}</div>
                <div className="text-xs mt-0.5" style={{ color: "var(--slate)" }}>
                  {t.category} · {t.severity} · {t.recurrence} · {t.legislation_reference}
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => openEdit(t)} data-testid={`admin-edit-${t.template_id}`}>Edit</Button>
              {t.active && (
                <Button variant="ghost" size="sm" onClick={() => deactivate(t)} style={{ color: "var(--alert)" }} data-testid={`admin-deactivate-${t.template_id}`}>
                  Deactivate
                </Button>
              )}
            </div>
          ))}
        </div>

        <TemplateDialog open={open} setOpen={setOpen} initial={editing} onSave={save} />
      </div>
    </AppShell>
  );
}

function TemplateDialog({ open, setOpen, initial, onSave }) {
  const isEdit = !!initial?.template_id;
  const [form, setForm] = useState({});
  useEffect(() => {
    setForm({
      title: initial?.title || "",
      description: initial?.description || "",
      legislation_reference: initial?.legislation_reference || "general — verify with strata lawyer",
      category: initial?.category || "records",
      recurrence: initial?.recurrence || "annual",
      default_due_rule: initial?.default_due_rule || "annual_from_registration:+365d",
      severity: initial?.severity || "important",
    });
  }, [initial, open]);
  const f = (k) => (v) => setForm((p) => ({ ...p, [k]: v?.target ? v.target.value : v }));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>{isEdit ? "Edit template" : "New template"}</DialogTitle></DialogHeader>
        <form onSubmit={(e) => { e.preventDefault(); onSave(form); }} className="space-y-3">
          <FormRow label="Title"><Input value={form.title || ""} onChange={f("title")} required data-testid="tpl-title" /></FormRow>
          <FormRow label="Description"><Textarea value={form.description || ""} onChange={f("description")} required data-testid="tpl-desc" /></FormRow>
          <FormRow label="Legislation reference"><Input value={form.legislation_reference || ""} onChange={f("legislation_reference")} required data-testid="tpl-legref" /></FormRow>
          <div className="grid grid-cols-3 gap-2">
            <FormRow label="Category"><Select value={form.category} onValueChange={f("category")}><SelectTrigger data-testid="tpl-cat"><SelectValue/></SelectTrigger><SelectContent>{CATS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select></FormRow>
            <FormRow label="Recurrence"><Select value={form.recurrence} onValueChange={f("recurrence")}><SelectTrigger data-testid="tpl-rec"><SelectValue/></SelectTrigger><SelectContent>{REC.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent></Select></FormRow>
            <FormRow label="Severity"><Select value={form.severity} onValueChange={f("severity")}><SelectTrigger data-testid="tpl-sev"><SelectValue/></SelectTrigger><SelectContent>{SEV.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></FormRow>
          </div>
          <FormRow label="Default due rule (e.g. annually:09-30, annual_from_registration:+365d)">
            <Input value={form.default_due_rule || ""} onChange={f("default_due_rule")} required data-testid="tpl-rule" />
          </FormRow>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button type="submit" style={{ background: "var(--blue)", color: "white" }} data-testid="tpl-save">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FormRow({ label, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>{label}</Label>
      {children}
    </div>
  );
}
